import { Link } from 'react-router-dom';
import { careSpan, careSentence, lifeLanes, layoutLanes, axisTicks, lifeTiles } from '../../lib/lifeInCare';

const Tile = ({ tile }) => (
  <Link to={tile.to} className="rounded-lg bg-ground hover:bg-inset transition-colors duration-state p-5 flex flex-col gap-1" data-testid={`life-tile-${tile.id}`}>
    <span className="pd-eyebrow">{tile.label}</span>
    <span className="font-heading text-[42px] font-bold tracking-[-0.035em] leading-none text-ink">{tile.value}</span>
    {tile.hint && <span className="text-sm text-ink-55">{tile.hint}</span>}
  </Link>
);

// One line of the strip: blocks sized by months on the shared axis, gaps
// left blank, and anything undated as a dashed "add when" placeholder.
const Lane = ({ lane, onlyUndated }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3" data-testid={`life-lane-${lane.id}`}>
    <span className="sm:w-[120px] shrink-0 text-[13px] font-semibold text-ink-55">{lane.label}</span>
    <div className="flex-1 min-w-0 flex gap-[3px] h-10">
      {!onlyUndated && lane.items.length > 0 && lane.blocks.map((b, i) => (b.gap ? (
        <span key={`gap-${i}`} style={{ flexGrow: b.months, flexBasis: 0 }} aria-hidden="true" />
      ) : (
        <Link
          key={b.id}
          to={b.href}
          style={{ flexGrow: b.months, flexBasis: 0 }}
          title={b.label}
          aria-label={b.label}
          className="min-w-[6px] rounded-md bg-surface hover:bg-inset transition-colors duration-state px-2.5 flex items-center overflow-hidden whitespace-nowrap text-[13px] font-semibold text-ink"
        >
          <span className="truncate" aria-hidden="true">{b.label}</span>
        </Link>
      )))}
      {(onlyUndated || !lane.items.length) && (lane.undated.length ? lane.undated.map((u) => (
        <Link key={u.id} to={u.href} className="flex-1 min-w-0 rounded-md border border-dashed border-shape px-2.5 flex items-center overflow-hidden whitespace-nowrap text-[13px] font-semibold text-ink">
          <span className="truncate">{u.label} · dates not added yet</span>
        </Link>
      )) : (
        <span className="flex-1 rounded-md border border-dashed border-shape px-2.5 flex items-center text-[13px] text-ink-55">None added yet</span>
      ))}
    </div>
  </div>
);

/**
 * "Your life in care" — the Overview's whole-life view: how long, a few
 * whole-life numbers, and one strip for who was responsible, where you
 * lived, school and health on a shared axis of years and ages. Everything
 * links to the home or organisation it stands for. Built from what the
 * person told us (lib/lifeInCare.js); it fills in as they add more.
 */
export const LifeInCare = ({ organisations, user }) => {
  const dob = user?.date_of_birth || null;
  const span = careSpan(organisations);
  const sentence = careSentence(span, dob);
  const tiles = lifeTiles(organisations, { dob, respitePool: user?.respite_carers || [] });
  const lanes = lifeLanes(organisations, dob);
  const layout = layoutLanes(lanes);
  const ticks = layout ? axisTicks(layout.start, layout.end, dob) : [];
  const undatedLanes = lanes.filter((l) => !l.items.length);

  return (
    <section aria-labelledby="life-heading" className="space-y-5" data-testid="life-section">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="min-w-0">
          <p className="pd-eyebrow">Your life in care</p>
          <h2 id="life-heading" className="mt-1 font-heading text-[24px] sm:text-[26px] font-semibold tracking-[-0.02em] leading-snug text-ink max-w-[60ch]" data-testid="life-sentence">
            {sentence || 'This fills in as you add the places you lived and the dates you were there.'}
          </h2>
        </div>
        {layout && <Link to="/my-life" className="text-[15px] font-semibold text-brand hover:text-brand-ink">Open My life</Link>}
      </div>

      {tiles.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {tiles.map((t) => <Tile key={t.id} tile={t} />)}
        </div>
      )}

      <div className="rounded-lg bg-ground p-4 sm:p-5 space-y-2.5" data-testid="life-strip">
        {layout && layout.lanes.map((l) => <Lane key={l.id} lane={l} />)}
        {!layout && undatedLanes.map((l) => <Lane key={l.id} lane={l} onlyUndated />)}
        {layout && ticks.length > 0 && (
          <div className="flex flex-col sm:flex-row sm:gap-3 pt-1" aria-hidden="true">
            <span className="sm:w-[120px] shrink-0" />
            <div className="flex-1 flex justify-between pd-mono">{ticks.map((t) => <span key={t.year}>{t.label}</span>)}</div>
          </div>
        )}
        {layout && dob === null && (
          <p className="text-sm text-ink-55">Add your date of birth in your profile to see how old you were.</p>
        )}
      </div>
    </section>
  );
};

export default LifeInCare;
